import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, CalendarClock, Trash2 } from "lucide-react";
import { api, rupiah, todayISO } from "../lib/dimsum";

const inputCls =
  "w-full rounded-2xl border-2 border-stone-300 bg-white px-3 py-3 font-semibold focus:border-rose-500 focus:outline-none";

const WaIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const Pinjaman = ({ teams = [], isMaster = false }) => {
  const [loans, setLoans] = useState([]);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [kind, setKind] = useState("pinjam");
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [openName, setOpenName] = useState(false);

  const participants = useMemo(
    () => teams.map((t) => t.members[0] || t.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [teams]
  );

  const load = useCallback(() => {
    api.get("/loans").then((r) => setLoans(r.data || [])).catch(() => setLoans([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const m = {};
    loans.forEach((l) => {
      const key = l.name;
      if (!m[key]) m[key] = { name: key, outstanding: 0, entries: [] };
      m[key].entries.push(l);
      m[key].outstanding += l.kind === "bayar" ? -(l.amount || 0) : l.amount || 0;
    });
    return Object.values(m)
      .map((g) => ({
        ...g,
        entries: g.entries.sort((a, b) => (b.created_at || b.date || "").localeCompare(a.created_at || a.date || "")),
      }))
      .sort((a, b) => b.outstanding - a.outstanding);
  }, [loans]);

  const fmtWhen = (l) => {
    const datePart = l.date
      ? new Date(`${l.date}T00:00:00`).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })
      : "";
    let timePart = "";
    if (l.created_at) {
      timePart = new Date(l.created_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" });
    }
    return timePart ? `${datePart} • ${timePart} WIB` : datePart;
  };

  const matchSearch = (nm) => nm.toLowerCase().includes(searchQ.trim().toLowerCase());
  const totalOutstanding = useMemo(() => groups.reduce((s, g) => s + Math.max(0, g.outstanding), 0), [groups]);
  const activeGroups = useMemo(
    () => groups.filter((g) => g.outstanding > 0 && matchSearch(g.name)),
    [groups, searchQ]
  );

  const shareLoanWa = (g) => {
    const pinjam = g.entries.filter((e) => e.kind !== "bayar").reduce((s, e) => s + (e.amount || 0), 0);
    const bayar = g.entries.filter((e) => e.kind === "bayar").reduce((s, e) => s + (e.amount || 0), 0);
    let text = "*RINCIAN HUTANG*\n\n";
    text += `Nama : ${g.name}\n\n`;
    const sorted = g.entries.slice().sort((a, b) => (a.created_at || a.date || "").localeCompare(b.created_at || b.date || ""));
    text += "Hutang:\n";
    sorted.filter((e) => e.kind !== "bayar").forEach((e) => {
      text += `${fmtWhen(e)} : +${rupiah(e.amount)}${e.note ? ` (${e.note})` : ""}\n`;
    });
    if (bayar > 0) {
      text += "\nPembayaran:\n";
      sorted.filter((e) => e.kind === "bayar").forEach((e) => {
        text += `${fmtWhen(e)} : -${rupiah(e.amount)}${e.note ? ` (${e.note})` : ""}\n`;
      });
    }
    text += `\nTotal Hutang : ${rupiah(pinjam)}\n`;
    text += `Total Bayar : ${rupiah(bayar)}\n`;
    text += `*Sisa Hutang : ${rupiah(Math.max(0, g.outstanding))}*\n`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const add = async () => {
    if (!name.trim()) return toast.error("Pilih peserta");
    if (!(Number(amount) > 0)) return toast.error("Jumlah harus lebih dari 0");
    if (kind === "bayar") {
      const owe = groups.find((g) => g.name.trim().toLowerCase() === name.trim().toLowerCase())?.outstanding || 0;
      if (owe <= 0) return toast.error(`${name.trim()} tidak punya hutang / sudah lunas`);
    }
    setSaving(true);
    try {
      await api.post("/loans", { name: name.trim(), amount: Number(amount), kind, date, note });
      toast.success(kind === "pinjam" ? "✅ Hutang dicatat" : "✅ Pembayaran dicatat");
      setAmount("");
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan (butuh login admin/master)");
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (e) => {
    const label = e.kind === "bayar" ? "pembayaran" : "hutang";
    if (!window.confirm(`Hapus catatan ${label} ${rupiah(e.amount)}${e.note ? ` (${e.note})` : ""}?`)) return;
    try {
      await api.delete(`/loans/${e.id}`);
      toast.success(`🗑️ Catatan ${label} dihapus`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus (butuh login admin/master)");
    }
  };

  const deleteAllForName = async (nm) => {
    if (!window.confirm(`Hapus SEMUA hutang & pembayaran milik "${nm}" secara permanen? Data ini akan hilang dari tab Hutang dan perhitungan Gaji.`)) return;
    if (!window.confirm(`Yakin hapus permanen hutang "${nm}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      const { data } = await api.delete(`/loans/by-name/${encodeURIComponent(nm)}`);
      toast.success(`🗑️ Hutang "${nm}" dihapus permanen (${data.deleted || 0} catatan)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus (khusus Master)");
    }
  };

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-6" data-testid="pinjaman-page">
      <div className="bg-white border-2 border-stone-200 rounded-3xl p-6 shadow-sm h-fit space-y-4">
        <h3 className="font-bold text-lg text-stone-900 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-rose-600" /> Catat Hutang / Bayar
        </h3>
        <div className="relative">
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Peserta</label>
          <input
            type="text"
            data-testid="pinjaman-name"
            value={name}
            placeholder="Ketik nama peserta…"
            onChange={(e) => { setName(e.target.value); setOpenName(true); }}
            onFocus={() => setOpenName(true)}
            onBlur={() => setTimeout(() => setOpenName(false), 150)}
            className={inputCls}
          />
          {openName && name.trim().length >= 1 && (() => {
            const q = name.trim().toLowerCase();
            const matches = participants.filter((p) => p.toLowerCase().includes(q) && p !== name);
            return matches.length > 0 ? (
              <ul
                data-testid="pinjaman-name-suggestions"
                className="absolute z-30 mt-1.5 w-full max-h-52 overflow-auto rounded-2xl border-2 border-stone-200 bg-white shadow-lg"
              >
                {matches.map((p) => (
                  <li key={p}>
                    <button
                      type="button"
                      data-testid={`pinjaman-name-option-${p}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setName(p); setOpenName(false); }}
                      className="w-full text-left px-4 py-2.5 font-semibold text-stone-800 hover:bg-rose-50 transition-colors"
                    >
                      {p}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null;
          })()}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="pinjaman-kind-pinjam"
            onClick={() => setKind("pinjam")}
            className={`min-h-[44px] rounded-2xl font-bold text-sm transition-colors ${kind === "pinjam" ? "bg-rose-600 text-white shadow-md" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
          >
            Hutang
          </button>
          <button
            type="button"
            data-testid="pinjaman-kind-bayar"
            onClick={() => setKind("bayar")}
            className={`min-h-[44px] rounded-2xl font-bold text-sm transition-colors ${kind === "bayar" ? "bg-emerald-600 text-white shadow-md" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
          >
            Bayar
          </button>
        </div>
        <div>
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Jumlah (Rp)</label>
          <input
            type="text"
            inputMode="numeric"
            data-testid="pinjaman-amount"
            value={amount ? Number(amount).toLocaleString("id-ID") : ""}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
            placeholder="0"
            className={`${inputCls} font-mono-num`}
          />
        </div>
        <div>
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Tanggal</label>
          <input type="date" data-testid="pinjaman-date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Catatan (opsional)</label>
          <input data-testid="pinjaman-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. kasbon, dll" className={inputCls} />
        </div>
        <button
          data-testid="pinjaman-save"
          onClick={add}
          disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 min-h-[48px] rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold disabled:opacity-50 active:scale-95 transition-transform"
        >
          <Plus className="w-4 h-4" /> Simpan
        </button>
        <p className="text-xs font-semibold text-stone-500">
          Catat hutang baru atau pembayaran (bayar) untuk peserta.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-stone-900">Hutang Peserta</h2>
          <div className="rounded-2xl bg-rose-50 border-2 border-rose-200 px-4 py-2" data-testid="pinjaman-total">
            <span className="text-xs font-bold uppercase tracking-wider text-rose-500">Total Sisa Hutang</span>
            <p className="font-mono-num font-extrabold text-rose-700 text-lg">{rupiah(totalOutstanding)}</p>
          </div>
        </div>

        <div className="bg-white border-2 border-stone-200 rounded-3xl p-4" data-testid="pinjaman-filter-bar">
          <label className="block mb-1 text-xs font-bold uppercase tracking-wider text-stone-500">Cari Peserta</label>
          <input
            data-testid="pinjaman-search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Ketik nama peserta…"
            className="w-full rounded-2xl border-2 border-stone-300 bg-white px-3 py-2.5 font-semibold focus:border-rose-500 focus:outline-none"
          />
        </div>

        {groups.length === 0 && (
          <p className="text-stone-400 font-semibold py-8 text-center">Belum ada catatan hutang.</p>
        )}

        {groups.length > 0 && activeGroups.length === 0 && (
          <p className="text-stone-400 font-semibold py-4 text-center">Tidak ada peserta dengan sisa hutang yang cocok.</p>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          {activeGroups.map((g) => (
            <div
              key={g.name}
              data-testid={`pinjaman-card-${g.name}`}
              className="bg-white border-2 border-stone-200 rounded-3xl p-4 shadow-sm space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-stone-900">{g.name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono-num font-extrabold text-rose-600">{rupiah(g.outstanding)}</span>
                  <button
                    data-testid={`pinjaman-wa-${g.name}`}
                    onClick={() => shareLoanWa(g)}
                    title="Bagikan rincian hutang ke WhatsApp"
                    className="grid place-items-center w-8 h-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-colors"
                  >
                    <WaIcon className="w-4 h-4" />
                  </button>
                  {isMaster && (
                    <button
                      data-testid={`pinjaman-delete-name-${g.name}`}
                      onClick={() => deleteAllForName(g.name)}
                      title="Hapus hutang permanen (Master)"
                      className="grid place-items-center w-8 h-8 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {g.entries.filter((e) => e.kind !== "bayar").length > 0 && (
                <div className="space-y-1 pt-1 border-t border-stone-100">
                  {g.entries
                    .filter((e) => e.kind !== "bayar")
                    .map((e) => (
                      <div
                        key={e.id}
                        data-testid={`pinjaman-hutang-when-${e.id}`}
                        className="flex items-center justify-between gap-2 text-xs text-stone-500"
                      >
                        <span className="flex items-center gap-1.5 min-w-0">
                          <CalendarClock className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                          <span className="truncate">{fmtWhen(e)}{e.note ? ` • ${e.note}` : ""}</span>
                        </span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          <span className="font-mono-num font-bold text-rose-600">+{rupiah(e.amount)}</span>
                          <button
                            data-testid={`pinjaman-delete-entry-${e.id}`}
                            onClick={() => deleteEntry(e)}
                            title="Hapus catatan ini"
                            className="grid place-items-center w-6 h-6 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-500 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      </div>
                    ))}
                </div>
              )}
              {g.entries.filter((e) => e.kind === "bayar").length > 0 && (
                <div className="space-y-1 pt-1 border-t border-stone-100">
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-600">Riwayat Pembayaran</p>
                  {g.entries
                    .filter((e) => e.kind === "bayar")
                    .map((e) => (
                      <div
                        key={e.id}
                        data-testid={`pinjaman-bayar-when-${e.id}`}
                        className="flex items-center justify-between gap-2 text-xs text-stone-500"
                      >
                        <span className="flex items-center gap-1.5 min-w-0">
                          <CalendarClock className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          <span className="truncate">{fmtWhen(e)}{e.note ? ` • ${e.note}` : ""}</span>
                        </span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          <span className="font-mono-num font-bold text-emerald-600">-{rupiah(e.amount)}</span>
                          <button
                            data-testid={`pinjaman-delete-entry-${e.id}`}
                            onClick={() => deleteEntry(e)}
                            title="Hapus catatan ini"
                            className="grid place-items-center w-6 h-6 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-500 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
